import { useState } from "react";
import type { Profile, ProfileImageKind, UpdateProfilePayload } from "api";
import cn from "classnames";
import { displayName } from "./profile.model";
import { ProfileModal } from "./ProfileModal";

type ProfileCardProps = {
  profile: Profile | null;
  onSave: (payload: UpdateProfilePayload) => Promise<string | null>;
  onUploadImage: (kind: ProfileImageKind, file: File) => Promise<string | null>;
  onRemoveImage: (kind: ProfileImageKind) => Promise<string | null>;
};

const PencilIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M13.5 3.8l2.7 2.7-8.2 8.2-3.4.7.7-3.4z" />
    <path d="M12.2 5.1l2.7 2.7" />
  </svg>
);

/**
 * The card at the head of the left column. The design makes it clickable for a
 * QClub member, whose profile is a page of its own; for an ordinary account
 * there is no such page, so only the pencil does anything.
 *
 * An empty card is still drawn while the profile loads — it holds its grid row
 * open, so the menu below it does not jump once the name arrives.
 */
export const ProfileCard = ({ profile, onSave, onUploadImage, onRemoveImage }: ProfileCardProps) => {
  const [editing, setEditing] = useState(false);

  if (profile === null) {
    return <div className="pc-me" />;
  }

  const headline = profile.headline?.trim() ?? "";

  return (
    <div className="pc-me">
      <div
        className={cn("pc-me__cover", { "has-img": profile.cover_url !== null })}
        style={profile.cover_url === null ? undefined : { backgroundImage: `url(${profile.cover_url})` }}
        role="img"
        aria-label="Cover image"
      >
        <span>cover 1128&times;191</span>
      </div>

      <button
        type="button"
        className="pc-me__edit"
        aria-label="Edit profile"
        title="Edit profile"
        onClick={() => setEditing(true)}
      >
        <PencilIcon />
      </button>

      <div className="pc-me__av">
        {profile.avatar_url === null ? <span>photo</span> : <img src={profile.avatar_url} alt="" />}
      </div>

      <div className="pc-me__b">
        <div className="pc-me__n">{displayName(profile)}</div>
        {headline !== "" && <p className="pc-me__tag">{headline}</p>}
      </div>

      {editing && (
        <ProfileModal
          profile={profile}
          onSave={onSave}
          onUploadImage={onUploadImage}
          onRemoveImage={onRemoveImage}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
};
